<template>
  <div v-if="show">
    <component
      :is="modalComponent[modal.component]"
      :show="show"
      v-bind="modal.properties"
      @update:show="
        (value) => {
          if (!value) {
            modal.component = '';
            modal.events = {};
            modal.properties = {};
            show = false;
          }
        }
      "
      v-on="{
        'update:show': (value) => {
          show = value;
        },
        ...modal.events,
      }"
    />
    <component
      :is="modalComponent[subModal.component]"
      :show="showSubModal"
      v-bind="subModal.properties"
      @update:show="
        (value) => {
          if (!value) {
            subModal.component = '';
            subModal.events = {};
            subModal.properties = {};
            showSubModal = false;
          }
        }
      "
      v-on="{
        'update:show': (value) => (showSubModal = value),
        ...subModal.events,
      }"
    />
  </div>
</template>

<script lang="ts" setup>
import { onBeforeMount, onBeforeUnmount, ref, nextTick } from "vue";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { MODAL_NAME } from "@/utils/constants";
import AddDomainModal from "../domains/addDomainModal.vue";
import ManageDomainModal from "../domains/manageDomainModal.vue";
import VerifyAddDomainModal from "../domains/verifyAddDomainModal.vue";
import CreateOrgModal from "../orgManagement/createOrgModal.vue";
import OfferAccessModal from "../orgManagement/offerAccessModal.vue";
import ProfileModal from "../orgManagement/profileModal.vue";
import RequestAccessDetailModal from "../orgManagement/requestAccessDetailModal.vue";
import ProcessRequestAccessDetailModal from "../orgManagement/processRequestAccessDetailModal.vue";
import MemberDetailsModal from "../team/members/memberDetailsModal.vue";
import AddRoleModal from "../team/roles/addRoleModal.vue";
import AddUserRoleModal from "../team/roles/addUserRoleModal.vue";
import { IModalMessage } from "@/interfaces/common/common";
import ModalAssessment from "../assessment/modalAssessment.vue";
import ModalAddNoteAssessment from "../assessment/modalAddNoteAssessment.vue";
import ModalEvidence from "../assessment/modalAddEvidenceAssessment.vue";
import ModalWizardMoreInfo from "../wizard/moreInfoModal.vue";
import SaveAndExitModal from "../wizard/saveAndExitModal.vue";
import AreYouSureModal from "../recommend/areYouSureModal.vue";
import CreateVendorAssessmentModal from "../vendor/createVendorAssessmentModal.vue";
import HandleAnauthModal from "../common/handleAnauthorizedModal.vue";

import DeleteModal from "../wizard/deleteModal.vue";
import EditActionModal from "../recommend/editActionModal.vue";
import AddNewActionModal from "../recommend/addNewActionModal.vue";
import ModalDomainNotBelongToOrg from "../wizard/modalDomainNotBelongToOrg.vue";

import ImageModal from "@/components/common/modalComponent.vue";
import ReorderYourActionModal from "../recommend/reorderYourActionModal.vue";
import ScheduleModal from "../scanning/scheduleModal.vue";
import VerifyOrgModal from "../orgManagement/verifyOrgModal.vue";
import ResendTokenModal from "../orgManagement/resendTokenModal.vue";
import MfaSelectionModal from "../team/members/mfaSelectionModal.vue";
import AddTotpModal from "../team/members/addMfaTotpModal.vue";
import VerifyResetPasswordModal from "../team/members/verifyResetPasswordModal.vue";
import ModalSecuringEvidence from "../assessment/modalSecuringEvidence.vue";
import ModalCompleteAssessment from "../assessment/modalCompleteAssessment.vue";
import UpdateAssessmentDetailModal from "../assessment/updateAssessmentDetailModal.vue";
import DuplicateAssessmentModal from "../assessment/duplicateAssessmentModal.vue";
import SecurityAndStabilityScoreModal from "../recommend/securityAndStabilityScoreModal.vue";
import DuplicateQuestionModal from "../assessment/duplicateQuestionModal.vue";

import ModalCreateNewVendorAssessment from "../vendor/createNewVendorAssessmentModal.vue";
import VendorAssessmentAttachmentModal from "../vendor/vendorAssessmentAttachmentModal.vue";
import ChangeRecipientVendorAssessmentModal from "../vendor/changeRecipientVendorAssessmentModal.vue";
import VendorUpdateAssessmentDetailModal from "../vendor/vendorUpdateAssessmentDetailModal.vue";
import VendorEditAssessmentDetailModal from "../vendor/editVendorAssessmentModal.vue";
import ManageVendorsModal from "../vendor/manageVendorsModal.vue";
import ManageVendorDetailModal from "../vendor/manageVendorDetailModal.vue";
import store from "@/store";
import { getComponentUiText } from "@/utils/utilities";

// Import component modal here.
const modalComponent = {};
modalComponent[MODAL_NAME.ADD_DOMAIN] = AddDomainModal;
modalComponent[MODAL_NAME.MANAGE_DOMAIN] = ManageDomainModal;
modalComponent[MODAL_NAME.VERIFY_ADD_DOMAIN] = VerifyAddDomainModal;
modalComponent[MODAL_NAME.CREATE_ORG] = CreateOrgModal;
modalComponent[MODAL_NAME.VERIFY_ORG] = VerifyOrgModal;
modalComponent[MODAL_NAME.OFFER_ACCESS] = OfferAccessModal;
modalComponent[MODAL_NAME.REQUEST_ACCESS] = RequestAccessDetailModal;
modalComponent[MODAL_NAME.ACCEPT_REJECT_REQUEST_ACCESS] =
  ProcessRequestAccessDetailModal;
modalComponent[MODAL_NAME.PROFILE] = ProfileModal;
modalComponent[MODAL_NAME.MEMBER_DETAIL] = MemberDetailsModal;
modalComponent[MODAL_NAME.ADD_ROLE] = AddRoleModal;
modalComponent[MODAL_NAME.ADD_USER_ROLE] = AddUserRoleModal;
modalComponent[MODAL_NAME.RESEND_TOKEN_MODAL] = ResendTokenModal;
modalComponent[MODAL_NAME.MFA_SELECTION_MODAL] = MfaSelectionModal;
modalComponent[MODAL_NAME.MODAL_HANDLE_ANAUTH] = HandleAnauthModal;

// Assessment
modalComponent[MODAL_NAME.MODAL_ADD_NOTE_ASSESSMENT] = ModalAddNoteAssessment;
modalComponent[MODAL_NAME.MODAL_ASSESSMENT] = ModalAssessment;
modalComponent[MODAL_NAME.MODAL_EVIDENCE] = ModalEvidence;
modalComponent[MODAL_NAME.MODAL_SECURING_EVIDENCE] = ModalSecuringEvidence;
modalComponent[MODAL_NAME.MODAL_COMPLETE_ASSESSMENT] = ModalCompleteAssessment;
modalComponent[MODAL_NAME.MODAL_UPDATE_ASSESSMENT_DETAIL] =
  UpdateAssessmentDetailModal;
modalComponent[MODAL_NAME.MODAL_DUPLICATE_ASSESSMENT] =
  DuplicateAssessmentModal;
modalComponent[MODAL_NAME.MODAL_DUPLICATE_QUESTION] = DuplicateQuestionModal;

// Wizard
modalComponent[MODAL_NAME.MODAL_WIZARD_MORE_INFO] = ModalWizardMoreInfo;
modalComponent[MODAL_NAME.MODAL_WIZARD_SAVE_EXIT] = SaveAndExitModal;
modalComponent[MODAL_NAME.MODAL_WIZARD_DELETE] = DeleteModal;
modalComponent[MODAL_NAME.MODAL_DOMAIN_NOT_BELONG_TO_ORG] =
  ModalDomainNotBelongToOrg;

// Recommend
modalComponent[MODAL_NAME.MODAL_RECOMMEN_ARE_YOU_SURE] = AreYouSureModal;
modalComponent[MODAL_NAME.MODAL_IMAGE] = ImageModal;
modalComponent[MODAL_NAME.MODAL_SECURITY_AND_STABILITY_SCORE] =
  SecurityAndStabilityScoreModal;

// Recommendation
modalComponent[MODAL_NAME.MODAL_RECOMMENDATION_EDIT_ACTIONS] = EditActionModal;
modalComponent[MODAL_NAME.MODAL_RECOMMENDATION_ADD_NEW_ACTION] =
  AddNewActionModal;
modalComponent[MODAL_NAME.MODAL_REORDER_ACTION_MODAL] = ReorderYourActionModal;

// Scanning
modalComponent[MODAL_NAME.MODAL_SCHEDULE] = ScheduleModal;

//Team
modalComponent[MODAL_NAME.MODAL_VERIFY_RESET_PASSWORD] =
  VerifyResetPasswordModal;

modalComponent[MODAL_NAME.MFA_ADD_TOTP_MODAL] = AddTotpModal;

//Vendor
modalComponent[MODAL_NAME.MODAL_CREATE_NEW_VENDOR_ASSESSMENT] =
  ModalCreateNewVendorAssessment;
modalComponent[MODAL_NAME.MODAL_CREATE_VENDOR_ASSESSMENT] =
  CreateVendorAssessmentModal;
modalComponent[MODAL_NAME.MODAL_VENDOR_ASSESSMENT_ATTACHMENT] =
  VendorAssessmentAttachmentModal;
modalComponent[MODAL_NAME.MODAL_CHANGE_RECIPIENT_VENDOR_ASSESSMENT] =
  ChangeRecipientVendorAssessmentModal;
modalComponent[MODAL_NAME.MODAL_VENDOR_UPDATE_ASSESSMENT_DETAIL] =
  VendorUpdateAssessmentDetailModal;
modalComponent[MODAL_NAME.MODAL_VENDOR_EDIT_ASSESSMENT_DETAIL] =
  VendorEditAssessmentDetailModal;
modalComponent[MODAL_NAME.MODAL_MANAGE_VENDOR] = ManageVendorsModal;
modalComponent[MODAL_NAME.MODAL_MANAGE_VENDOR_DETAIL] = ManageVendorDetailModal;

const show = ref(false);
const showSubModal = ref(false);
const modal: IModalMessage = { component: "", events: {} };
const subModal: IModalMessage = { component: "", events: {} };

onBeforeMount(() => {
  listeningEmitEvent();
});

onBeforeUnmount(() => {
  removeEmitEventListener();
});
const listeningEmitEvent = () => {
  EventEmitter.on(EmitterEvents.OPEN_MODAL, showModal);
};

const removeEmitEventListener = () => {
  EventEmitter.off(EmitterEvents.OPEN_MODAL);
};

const showModal = async (value) => {
  if (value.component === MODAL_NAME.MODAL_RECOMMEN_ARE_YOU_SURE) {
    store.dispatch("LoadingAreYouSureButtonModal/setLoadingRedButton", false);
  }
  if (value.component === MODAL_NAME.MODAL_RECOMMENDATION_EDIT_ACTIONS) {
    nextTick(() => {
      setTimeout(() => {
        if (value.properties?.shouldScrollToBottom) {
          const button = document.querySelector(
            "#addStep",
          ) as HTMLButtonElement;
          if (button) {
            button.click();
          }

          setTimeout(() => {
            const modalContent = document.querySelector(".modalContainer");

            if (modalContent) {
              modalContent.scrollTo({
                top: modalContent.scrollHeight,
                behavior: "smooth",
              });
            }
            setTimeout(() => {
              document.getElementById("input-focus")?.focus();
            }, 100);
          }, 100);
        }
      }, 200);
    });
  }

  // prevent duplicate modal
  if (
    modal.component == value.component &&
    show.value &&
    modal.component === MODAL_NAME.MODAL_RECOMMEN_ARE_YOU_SURE &&
    value.properties.title ===
      getComponentUiText("app-subscription-required-modal-title-1")
  ) {
    return;
  }
  if (show.value) {
    subModal.component = value.component;
    subModal.events = value.events;
    subModal.properties = { ...value.properties, isHideOverlay: false };

    if (subModal.component in modalComponent) {
      await new Promise((resolve) => setTimeout(resolve, 10));
      showSubModal.value = true;
    }
    return;
  }
  modal.component = value.component;
  modal.events = value.events;
  modal.properties = value.properties;
  if (modal.component in modalComponent) {
    await new Promise((resolve) => setTimeout(resolve, 10));
    show.value = true;
  }
};
</script>
